import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { track } from './track';

interface TrackProps {
  id: string;
  threshold?: number;
  eventName?: string;
  getEventParameters?: (context: TrackEventContext) => Record<string, unknown>;
  children: ReactNode;
}

export interface TrackEventContext {
  id: string;
  duration?: number;
}

// Track viewed component and how long it's been visible in viewport.
export const Track = ({
  id,
  children,
  threshold = 0.2,
  eventName = 'component_view',
  getEventParameters = getDefaultEventParameters, // Ensure stable ref is passed
}: TrackProps) => {
  const startTimeRef = useRef<Date | undefined>(undefined);

  const trackEvent = useCallback(() => {
    if (!startTimeRef.current) {
      return;
    }
    const duration = new Date().getTime() - startTimeRef.current.getTime();
    startTimeRef.current = undefined;
    track(eventName, getEventParameters({ id, duration }));
  }, [id, eventName, getEventParameters]);

  const { ref } = useInView({
    threshold,
    onChange: (isInView) => {
      if (isInView) {
        startTimeRef.current = new Date();
      } else if (startTimeRef.current) {
        trackEvent();
      }
    },
  });

  // Track when unmounting component
  useEffect(() => {
    return () => {
      trackEvent();
    };
  }, [trackEvent]);

  return <div ref={ref}>{children}</div>;
};

function getDefaultEventParameters({ id, duration }: TrackEventContext) {
  return { component_id: id, duration };
}
